import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { adminLoginSchema, type AdminLoginInput } from "@shudian/contracts";
import { LockKeyhole, ShieldCheck, UserRoundCheck } from "lucide-react";
import { useAdminAuth } from "../auth/useAdminAuth.js";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { Field } from "../components/ui/Field.js";
import { Alert } from "../components/ui/Alert.js";
import { CloudApiError, describeError } from "../lib/errors.js";

interface LocationState {
  from?: string;
}

const DEV_ADMIN_DEFAULTS: AdminLoginInput = import.meta.env.DEV
  ? {
      email: "admin@shudian.local",
      password: "shudian-admin-dev"
    }
  : {
      email: "",
      password: ""
    };

export function LoginPage() {
  const { login, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<AdminLoginInput>({
    resolver: zodResolver(adminLoginSchema),
    defaultValues: DEV_ADMIN_DEFAULTS
  });

  if (isAuthenticated) {
    return <Navigate to={(location.state as LocationState | null)?.from ?? "/"} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setErrorMessage(null);
    try {
      await login(values);
      navigate((location.state as LocationState | null)?.from ?? "/", { replace: true });
    } catch (error) {
      const message = error instanceof CloudApiError ? describeError(error.code) : "登录失败";
      setErrorMessage(message);
    }
  });

  return (
    <div className="grid min-h-screen grid-cols-1 bg-surface-base lg:grid-cols-[minmax(0,1fr)_440px]">
      <section className="hidden border-r border-surface-border bg-surface-panel px-10 py-10 lg:flex lg:flex-col lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded bg-text-primary text-sm font-semibold text-white">
              SD
            </span>
            <div>
              <div className="text-base font-semibold text-text-primary">数店云端管理</div>
              <div className="text-xs text-text-muted">Cloud Admin Console</div>
            </div>
          </div>
          <div className="mt-16 max-w-2xl">
            <div className="text-xs font-medium text-text-muted">后台入口</div>
            <h1 className="mt-2 text-3xl font-semibold leading-tight text-text-primary">
              运营、授权、发布和审计集中管理。
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-6 text-text-secondary">
              用于处理客户续费、订单核对、规则发布、采集代理池和桌面端更新。敏感密钥只在云端 API 服务端配置，管理端仅展示状态和操作入口。
            </p>
          </div>
        </div>
        <div className="grid max-w-2xl grid-cols-3 gap-3">
          <SecurityItem icon={<ShieldCheck className="h-4 w-4" />} title="权限控制" text="管理员会话独立签发" />
          <SecurityItem icon={<LockKeyhole className="h-4 w-4" />} title="密钥隔离" text="不在前端保存私钥" />
          <SecurityItem icon={<UserRoundCheck className="h-4 w-4" />} title="审计留痕" text="关键操作记录审计" />
        </div>
      </section>

      <main className="flex min-h-screen items-center justify-center px-5 py-8">
        <div className="w-full max-w-sm rounded border border-surface-border bg-surface-panel shadow-sm">
          <div className="border-b border-surface-border px-5 py-4">
            <div className="flex items-center gap-3 lg:hidden">
              <span className="grid h-8 w-8 place-items-center rounded bg-text-primary text-xs font-semibold text-white">
                SD
              </span>
              <div>
                <h1 className="text-base font-semibold text-text-primary">数店云端管理</h1>
                <p className="mt-0.5 text-xs text-text-secondary">Cloud Admin Console</p>
              </div>
            </div>
            <div className="hidden lg:block">
              <h1 className="text-base font-semibold text-text-primary">管理员登录</h1>
              <p className="mt-0.5 text-xs text-text-secondary">仅供平台管理员使用，请妥善保管账号。</p>
            </div>
          </div>
          <form className="space-y-3 px-5 py-4" onSubmit={onSubmit} noValidate>
          {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}
          <Field
            label="邮箱"
            htmlFor="email"
            required
            error={errors.email?.message}
          >
            <Input
              id="email"
              type="email"
              autoComplete="email"
              invalid={!!errors.email}
              {...register("email")}
            />
          </Field>
          <Field
            label="密码"
            htmlFor="password"
            required
            error={errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={!!errors.password}
              {...register("password")}
            />
          </Field>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "登录中…" : "登录"}
          </Button>
        </form>
          <div className="border-t border-surface-border px-5 py-3 text-xs leading-5 text-text-muted">
            签名私钥仅在云端 API 服务端配置；本端不持有任何签名密钥。
          </div>
        </div>
      </main>
    </div>
  );
}

function SecurityItem({
  icon,
  title,
  text
}: {
  icon: ReactNode;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded border border-surface-border bg-surface-raised px-3 py-3">
      <div className="grid h-8 w-8 place-items-center rounded bg-accent-subtle text-accent">{icon}</div>
      <div className="mt-3 text-sm font-medium text-text-primary">{title}</div>
      <div className="mt-1 text-xs leading-5 text-text-muted">{text}</div>
    </div>
  );
}
